import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { devAuthUser } from '@config/env';
import { useApiClient } from '@core/api/useApiClient';
import { useConsentStore } from './consentStore';

/**
 * Mirror a signed-in user's consent choice to the BE (`POST /api/v1/consent`) so it's demonstrable
 * (GDPR). Anonymous/pre-auth users keep the choice in localStorage only (there's no account to key it
 * to). Fire-once per distinct decision; best-effort (a failed POST just retries on the next change —
 * the localStorage record is still the source of truth for gating).
 */
export function useConsentSync(): void {
  const api = useApiClient();
  const consent = useConsentStore((s) => s.consent);

  // AuthProvider isn't mounted in dev-auth mode (useAuth would throw); mirror RealtimeProvider's guard.
  // devAuthUser is a module-level constant, so the conditional hook call order is stable per render.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isAuthenticated = devAuthUser ? true : useAuth().isAuthenticated;

  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !consent) return;
    const sig = `${consent.version}:${consent.analytics}`;
    if (lastSentRef.current === sig) return; // already recorded this exact decision
    lastSentRef.current = sig;
    void api
      .post<void>('/api/v1/consent', { policyVersion: consent.version, analytics: consent.analytics })
      .catch(() => { lastSentRef.current = null; }); // allow a retry on the next change
  }, [isAuthenticated, consent, api]);
}
